import type { ColumnFiltersState } from '@tanstack/react-table';
import { useCallback, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router';

export type FilterUrlMapping = {
  columnId: string;
  urlParam: string;
  // Convert from URL value to filter value
  urlToFilter?: (urlValue: string) => string | string[];
  // Convert from filter value to URL value
  filterToUrl?: (filterValue: string | string[]) => string;
  // Whether this filter supports multiple values
  isMulti?: boolean;
};

export function useUrlFilterSync(
  columnFilters: ColumnFiltersState,
  setColumnFilters: (
    filters:
      | ColumnFiltersState
      | ((old: ColumnFiltersState) => ColumnFiltersState),
  ) => void,
  mappings: FilterUrlMapping[],
) {
  const [searchParameters, setSearchParameters] = useSearchParams();
  const isInitialLoad = useRef(true);

  // Sync URL parameters to filters on mount and URL change
  useEffect(() => {
    const urlBasedFilters: ColumnFiltersState = [];

    for (const { columnId, urlParam, urlToFilter, isMulti } of mappings) {
      const urlValue = searchParameters.get(urlParam);

      if (urlValue) {
        let filterValue: string | string[];

        if (isMulti) {
          // Handle multiple values as separate URL parameters with the same key
          const allValues = searchParameters.getAll(urlParam);
          filterValue = allValues.map((value) => {
            const result = urlToFilter ? urlToFilter(value) : value;
            // Ensure each value is a string
            return Array.isArray(result) ? result.join(',') : result;
          });
        } else {
          filterValue = urlToFilter ? urlToFilter(urlValue) : urlValue;
        }

        urlBasedFilters.push({
          id: columnId,
          value: filterValue,
        });
      }
    }

    // Only update if there are URL-based filters or existing filters to clear
    if (
      urlBasedFilters.length > 0 ||
      columnFilters.some((filter) =>
        mappings.some((mapping) => mapping.columnId === filter.id),
      )
    ) {
      setColumnFilters((old) => [
        // Keep filters not managed by URL
        ...old.filter(
          (filter) =>
            !mappings.some((mapping) => mapping.columnId === filter.id),
        ),
        // Add URL-based filters
        ...urlBasedFilters,
      ]);
    }

    // Mark initial load as complete
    isInitialLoad.current = false;
  }, [searchParameters]); // Only depend on searchParams, not columnFilters to avoid loops

  // Sync filters to URL parameters
  const syncFiltersToUrl = useCallback(() => {
    setSearchParameters((parameters) => {
      const newParameters = new URLSearchParams(parameters);

      // Clear all managed URL parameters first
      for (const { urlParam } of mappings) {
        newParameters.delete(urlParam);
      }

      // Set parameters based on active filters
      for (const filter of columnFilters) {
        const mapping = mappings.find((m) => m.columnId === filter.id);
        if (mapping && filter.value) {
          if (Array.isArray(filter.value)) {
            // Handle multiple values - add each as a separate parameter
            for (const value of filter.value) {
              const urlValue = mapping.filterToUrl
                ? mapping.filterToUrl(value)
                : String(value);
              if (urlValue) {
                newParameters.append(mapping.urlParam, urlValue);
              }
            }
          } else {
            const urlValue = mapping.filterToUrl
              ? mapping.filterToUrl(filter.value as string | string[])
              : String(filter.value);
            if (urlValue) {
              newParameters.set(mapping.urlParam, urlValue);
            }
          }
        }
      }

      return newParameters;
    });
  }, [columnFilters, setSearchParameters, mappings]);

  // Sync filters to URL parameters whenever filters change (but not on initial load)
  useEffect(() => {
    if (isInitialLoad.current) {
      return;
    }
    syncFiltersToUrl();
  }, [columnFilters, syncFiltersToUrl]);

  // Clear all filters and URL parameters
  const clearAllFilters = useCallback(() => {
    setColumnFilters([]);
    setSearchParameters((parameters) => {
      const newParameters = new URLSearchParams(parameters);
      for (const { urlParam } of mappings) {
        newParameters.delete(urlParam);
      }
      return newParameters;
    });
  }, [setColumnFilters, setSearchParameters, mappings]);

  return {
    syncFiltersToUrl,
    clearAllFilters,
  };
}
